/**
 * Authoritative photo-reference maintenance (1.63.x correctness release).
 *
 * A captured photo is referenced in exactly two places:
 *   - `vehicle_part_inspections.photo_urls` (walkaround + per-part photos)
 *   - `vehicle_appraisals.additional_photos`
 *
 * Production incident (J-1072 POD): the delete/replace path removed the local
 * durable record AND the Storage object but left the database reference behind.
 * The completion workflow then waited forever on a path that can never exist —
 * "3 referenced / 2 in Storage" — and no POD PDF or email was ever produced.
 *
 * Pruning is therefore part of the delete lifecycle. It is:
 *  - EXACT: only the supplied paths are removed, never a prefix or a sibling;
 *  - SCOPED: only rows belonging to the given appraisal are touched;
 *  - IDEMPOTENT: repeating it is a no-op that reports success.
 */

import { supabase } from "@/integrations/supabase/client";

/** Normalise a storage reference to the stored form (no leading slashes). */
export function normalisePhotoPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().replace(/^\/+/, "");
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Remove a set of exact Storage paths from every database reference belonging
 * to `appraisalId`. Returns the number of rows actually changed (0 when the
 * references were already absent — the idempotent case).
 */
export async function prunePhotoReferences(
  appraisalId: string,
  paths: readonly (string | null | undefined)[],
): Promise<number> {
  const targets = new Set(
    paths.map(normalisePhotoPath).filter((p): p is string => !!p),
  );
  if (!appraisalId || targets.size === 0) return 0;

  let changedRows = 0;

  // ── vehicle_part_inspections.photo_urls ──
  const { data: inspections, error: inspError } = await supabase
    .from("vehicle_part_inspections")
    .select("id, photo_urls")
    .eq("appraisal_id", appraisalId);
  if (inspError) throw inspError;

  for (const inspection of inspections || []) {
    const current = Array.isArray(inspection.photo_urls) ? inspection.photo_urls : [];
    const next = current.filter((value) => {
      const path = normalisePhotoPath(value);
      return !path || !targets.has(path);
    });
    if (next.length === current.length) continue; // nothing of ours in this row
    const { error } = await supabase
      .from("vehicle_part_inspections")
      .update({ photo_urls: next })
      .eq("id", inspection.id);
    if (error) throw error;
    changedRows += 1;
  }

  // ── vehicle_appraisals.additional_photos ──
  const { data: appraisal, error: appraisalError } = await supabase
    .from("vehicle_appraisals")
    .select("additional_photos")
    .eq("id", appraisalId)
    .maybeSingle();
  if (appraisalError) throw appraisalError;

  const existing = Array.isArray(appraisal?.additional_photos) ? appraisal!.additional_photos : [];
  const nextAdditional = existing.filter((value) => {
    const path = normalisePhotoPath(value);
    return !path || !targets.has(path);
  });
  if (nextAdditional.length !== existing.length) {
    const { error } = await supabase
      .from("vehicle_appraisals")
      .update({ additional_photos: nextAdditional })
      .eq("id", appraisalId);
    if (error) throw error;
    changedRows += 1;
  }

  return changedRows;
}
